import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { getLogo, logoProvider, letterTileForUrl } from 'helpers/sourceIcon';
import SourceDefault from 'helpers/source-default.svg';
import { Icon, ICONS } from 'components/Icons';
import ToolTip from 'components/elements/atoms/ToolTip/ToolTip';

import './SourceLogo.scss';

export const getSourceLogo = (url) => {
  try {
    const sourceURL = new URL(url);
    return getLogo({ domain: sourceURL.host });
  } catch (error) {
    console.log('Error while parsing url', url, error);
    return SourceDefault;
  }
};

export const getTooltipMessage = (
  sourceStatus,
  source,
  showLimitedToCourse,
) => {
  switch (sourceStatus) {
    case 'approved':
      return {
        message: `${
          source || 'This source'
        } is an approved source, and this article is compatible with AverPoint\'s browser extension.`,
        color: 'green',
      };
    case 'limitedToCourse':
      return {
        message: `${
          source || 'This source'
        } is not an approved source in AverPoint, but the browser extension will log reading and allow the Credibility Layer for students in this class.`,
        color: 'yellow',
      };
    default:
      return {
        message: `${
          source || 'This source'
        } is not an approved source in AverPoint`,
        color: 'red',
      };
  }
};

const cache = {};

export default function SourceLogo({
  url,
  setSourceName,
  isLoading,
  showLimitedToCourse,
  displaySourceName,
  showAverPointReaderLogo = false,
}) {
  const [loading, setLoading] = useState(false);
  const [sourceStatus, setSourceStatus] = useState(null);
  const [source, setSource] = useState('');
  // The favicon service answers 404 for a domain it does not know, so with a
  // list this long some will miss. A monogram beats a broken-image glyph.
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    setLogoFailed(false);
  }, [url]);

  useEffect(() => {
    if (
      !url ||
      url.includes('averpoint.com') ||
      url.includes('localhost:3000')
    ) {
      setSourceStatus('approved');
      setSource('AverPoint Reader');

      return;
    }
    setLoading(true);

    let promise;

    if (cache[url]) {
      promise = Promise.resolve(cache[url]);
    } else {
      promise = axios({
        method: 'get',
        url: `${process.env.REACT_APP_SOURCE_PROVIDER_LAMBDA}/default?searchterm=${url}&type=sourceURL`,
      }).then((response) => {
        cache[url] = response.data;
        return response.data;
      });
    }

    promise
      .then((data) => {
        if (setSourceName) {
          setSourceName(data.name);
        }
        setSource(data.name);
        setSourceStatus(data.sourceApprovalStatus);
      })
      .catch((error) => {
        console.error('Error while checking source approval status', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isLoading]);

  const { message, color } = getTooltipMessage(
    sourceStatus,
    source,
    showLimitedToCourse,
  );

  return (
    <span className="reading-source-logo">
      <div className="image-container">
        <ToolTip toolTipText={loading ? null : message} toolTipPosition="right">
          <img
            src={logoFailed ? letterTileForUrl(url) : getSourceLogo(url)}
            alt="source logo"
            onError={() => setLogoFailed(true)}
            // Two ways a favicon can be a miss, and they need different
            // hooks. The service answers 404 for an unknown domain, which
            // fires onError - but the 404 body is a VALID 16x16 globe, and a
            // browser that renders it instead would fire onLoad. Asking for
            // 128 and getting 16 back is that globe, so treat it as a miss
            // too rather than showing a 4x-upscaled placeholder.
            onLoad={(e) => {
              if (!logoFailed && e.target.naturalWidth <= 16) setLogoFailed(true);
            }}
          />
          {displaySourceName && source && (
            <span className="ml-2 source-name">{source}</span>
          )}
          {!loading && (
            <div className={`status-check ${color}`}>
              <Icon icon={ICONS.CHECK} size={10} />
            </div>
          )}
          {showAverPointReaderLogo && (
            <img
              src={`${logoProvider}averpoint.com`}
              className="averpoint-logo"
              alt="averpoint logo"
            />
          )}
        </ToolTip>
      </div>
    </span>
  );
}
